import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import DocumentoClienteFields from '../common/DocumentoClienteFields';
import React, { useState, useEffect } from 'react';
import { Cliente, updateCliente } from '../../services/clienteService';
import ImageUpload from '../Upload/ImageUpload';
import DeclaracionJuradaUpload from '../Upload/DeclaracionJuradaUpload';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    cliente: Cliente | null;
    onUpdate: (updated: Cliente) => void;
}

const EditClienteModal: React.FC<Props> = ({ isOpen, onClose, cliente, onUpdate }) => {
    const [form, setForm] = useState({
        telefono: '',
        ci_nit: '', codigo_tipo_documento_identidad_sin: 1, complemento: '',
    });
    const [foto, setFoto] = useState<File | null>(null);
    const [fotoDeclaracion, setFotoDeclaracion] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (cliente) {
            setForm({
                telefono: cliente.telefono || '',
                ci_nit: cliente.ci_nit || '', codigo_tipo_documento_identidad_sin: cliente.codigo_tipo_documento_identidad_sin || 1, complemento: cliente.complemento || '',
            });
            setFoto(null);
            setFotoDeclaracion(null);
        }
        setError('');
    }, [cliente, isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!cliente) {
            setError('Cliente no válido');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const updated = await updateCliente(cliente.id, {
                telefono: form.telefono,
                ci_nit: form.ci_nit, codigo_tipo_documento_identidad_sin: form.codigo_tipo_documento_identidad_sin, complemento: form.complemento,
                foto: foto || undefined,
                foto_declaracion_jurada: fotoDeclaracion || undefined,
                declaracion_jurada_firmada: fotoDeclaracion ? true : cliente.declaracion_jurada_firmada,
            });
            onUpdate(updated);
            onClose();
        } catch (err) {
            /* Browser diagnostics omitted. */
            setError('Error al actualizar el cliente');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !cliente) return null;

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />
            <LumaAccessiblePanel coworkTitle="Editar · Clientes" data-luma-dialog="true" className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-start mb-4">
                    <LumaAccessibleTitle className="text-xl font-bold text-gray-900">Editar Cliente</LumaAccessibleTitle>
                    <div className="flex items-center gap-4">
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Teléfono
                            </label>
                            <input
                                name="telefono"
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={form.telefono}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                CI/NIT
                            </label>
                            <input
                                name="ci_nit"
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={form.ci_nit}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <DocumentoClienteFields codigo={form.codigo_tipo_documento_identidad_sin} complemento={form.complemento} onChange={(codigo, complemento) => setForm({ ...form, codigo_tipo_documento_identidad_sin: codigo, complemento })} className="w-full border rounded-lg px-3 py-2" />

                    {/* Declaración Jurada y Firma Fotográfica */}
                    <div className="mb-4">
                        <DeclaracionJuradaUpload
                            setFile={(file) => setFotoDeclaracion(file)}
                            currentImageUrl={cliente.foto_declaracion_jurada_url}
                            isSigned={cliente.declaracion_jurada_firmada}
                            fechaFirma={cliente.fecha_declaracion_jurada}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                            Foto de perfil (opcional)
                        </label>
                        <ImageUpload
                            setImagen={(file) => setFoto(file)}
                            currentImage={cliente.foto_url ?? undefined}
                        />
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 text-sm bg-[#3564db] text-white rounded-lg transition-colors font-medium flex items-center ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8f1f64]'
                                }`}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Guardar Cambios'
                            )}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default EditClienteModal;

